/**
 * Mock API for B1.9 InvoiceList.
 *
 * Mirrors the backend contract so that Claude'un teslim ettiği ince wrapper
 * gerçek backend'e yöneldiğinde (Faz 2.x) page/hooks değişmeden geçiş yapsın.
 * Latency simülasyonu: 200-400ms (`SIMULATED_LATENCY_MS`).
 *
 * Backend karşılıkları (Faz 1.3):
 *   listInvoices      → GET  /api/v1/business/invoices
 *   issueInvoice      → POST /api/v1/business/invoices/:id/issue
 *   sendInvoice       → POST /api/v1/business/invoices/:id/send
 *   voidInvoice       → POST /api/v1/business/invoices/:id/void
 *   downloadInvoice   → GET  /api/v1/business/invoices/:id/download
 */

import { SEED_BRANCH_ID, SEED_BRANCH_NAME, SEED_INVOICE_ROWS } from './seeds';
import type {
  DownloadInvoiceRequest,
  InvoiceDeliveryStatus,
  InvoiceListFilters,
  InvoiceListSnapshot,
  InvoiceListTotals,
  InvoiceRow,
  InvoiceStatus,
  IssueInvoiceRequest,
  ListInvoicesRequest,
  SendInvoiceRequest,
  VoidInvoiceRequest,
} from './types';

const SIMULATED_LATENCY_MS = 280;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

const mutableStore: Map<string, InvoiceRow> = new Map(
  SEED_INVOICE_ROWS.map((row) => [row.id, row]),
);

export const DEFAULT_LIST_FILTERS: InvoiceListFilters = {
  search: '',
  status: [],
  type: [],
  dateRange: null,
  customerId: null,
  sort: { field: 'issuedAt', dir: 'desc' },
  page: 0,
  pageSize: 25,
};

function mergeFilters(
  partial: Partial<InvoiceListFilters> | undefined,
): InvoiceListFilters {
  if (!partial) return DEFAULT_LIST_FILTERS;
  return {
    ...DEFAULT_LIST_FILTERS,
    ...partial,
    sort: partial.sort ?? DEFAULT_LIST_FILTERS.sort,
  };
}

function matchesFilter(row: InvoiceRow, filters: InvoiceListFilters): boolean {
  const q = filters.search.trim().toLowerCase();
  if (q.length > 0) {
    const haystack = [
      row.invoiceNumber ?? '',
      row.customer.name,
      row.customer.email ?? '',
      row.customer.phone ?? '',
      row.customer.taxNumber ?? '',
    ]
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  if (filters.status.length > 0 && !filters.status.includes(row.status)) {
    return false;
  }
  if (filters.type.length > 0 && !filters.type.includes(row.type)) {
    return false;
  }
  if (filters.customerId && row.customer.id !== filters.customerId) {
    return false;
  }
  if (filters.dateRange) {
    const refDate = row.issuedAt ?? row.createdAt;
    const d = refDate.slice(0, 10);
    if (d < filters.dateRange.from || d > filters.dateRange.to) return false;
  }
  return true;
}

function compareRows(
  a: InvoiceRow,
  b: InvoiceRow,
  filters: InvoiceListFilters,
): number {
  const { field, dir } = filters.sort;
  const mul = dir === 'asc' ? 1 : -1;
  switch (field) {
    case 'issuedAt': {
      const va = a.issuedAt ?? a.createdAt;
      const vb = b.issuedAt ?? b.createdAt;
      return (va < vb ? -1 : va > vb ? 1 : 0) * mul;
    }
    case 'totalMinor':
      return (a.totalMinor - b.totalMinor) * mul;
    case 'customerName':
      return a.customer.name.localeCompare(b.customer.name, 'tr') * mul;
    case 'invoiceNumber': {
      const va = a.invoiceNumber ?? '';
      const vb = b.invoiceNumber ?? '';
      return va.localeCompare(vb) * mul;
    }
    case 'dueAt': {
      const va = a.dueAt ?? '9999';
      const vb = b.dueAt ?? '9999';
      return va.localeCompare(vb) * mul;
    }
    default:
      return 0;
  }
}

function computeTotals(rows: readonly InvoiceRow[]): InvoiceListTotals {
  return rows.reduce<InvoiceListTotals>(
    (acc, row) => ({
      totalCount: acc.totalCount + 1,
      draftCount: acc.draftCount + (row.status === 'draft' ? 1 : 0),
      issuedCount: acc.issuedCount + (row.status === 'issued' ? 1 : 0),
      paidCount: acc.paidCount + (row.status === 'paid' ? 1 : 0),
      failedCount: acc.failedCount + (row.status === 'failed' ? 1 : 0),
      grossMinor: acc.grossMinor + row.subtotalMinor + row.discountMinor,
      discountMinor: acc.discountMinor + row.discountMinor,
      taxMinor: acc.taxMinor + row.taxMinor,
      netMinor: acc.netMinor + row.totalMinor,
      paidMinor: acc.paidMinor + row.paidMinor,
      balanceMinor: acc.balanceMinor + row.balanceMinor,
    }),
    {
      totalCount: 0,
      draftCount: 0,
      issuedCount: 0,
      paidCount: 0,
      failedCount: 0,
      grossMinor: 0,
      discountMinor: 0,
      taxMinor: 0,
      netMinor: 0,
      paidMinor: 0,
      balanceMinor: 0,
    },
  );
}

function allRows(): InvoiceRow[] {
  return Array.from(mutableStore.values());
}

function buildSnapshot(filters: InvoiceListFilters): InvoiceListSnapshot {
  const filtered = allRows()
    .filter((r) => matchesFilter(r, filters))
    .sort((a, b) => compareRows(a, b, filters));
  const start = filters.page * filters.pageSize;
  const end = start + filters.pageSize;
  const page = filtered.slice(start, end);
  return {
    branchId: SEED_BRANCH_ID,
    branchName: SEED_BRANCH_NAME,
    filters,
    totals: computeTotals(filtered),
    rows: page,
    hasMore: end < filtered.length,
  };
}

export async function listInvoices(
  req: ListInvoicesRequest,
): Promise<InvoiceListSnapshot> {
  await wait(SIMULATED_LATENCY_MS);
  if (req.branchId !== SEED_BRANCH_ID) {
    return {
      branchId: req.branchId,
      branchName: '',
      filters: mergeFilters(req.filters),
      totals: computeTotals([]),
      rows: [],
      hasMore: false,
    };
  }
  return buildSnapshot(mergeFilters(req.filters));
}

export async function getInvoiceSnapshot(
  branchId: string,
  filters?: Partial<InvoiceListFilters>,
): Promise<InvoiceListSnapshot> {
  return listInvoices({ branchId, filters: filters ?? {} });
}

function nextInvoiceNumber(type: 'e_arsiv' | 'e_fatura'): string {
  const prefix = type === 'e_fatura' ? 'EF' : 'EA';
  const todayDigits = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const existing = allRows()
    .map((r) => r.invoiceNumber ?? '')
    .filter((n) => n.startsWith(`${prefix}${todayDigits}`));
  const serial = String(existing.length + 1).padStart(6, '0');
  return `${prefix}${todayDigits}${serial}`;
}

export async function issueInvoice(
  req: IssueInvoiceRequest,
): Promise<InvoiceRow> {
  await wait(SIMULATED_LATENCY_MS);
  const row = mutableStore.get(req.invoiceId);
  if (!row) throw new Error(`Invoice not found: ${req.invoiceId}`);
  if (row.status !== 'draft' && row.status !== 'failed') {
    throw new Error(`Invoice ${req.invoiceId} cannot be issued from ${row.status}`);
  }
  const issuedAt = new Date().toISOString();
  const issued: InvoiceRow = {
    ...row,
    invoiceNumber: nextInvoiceNumber(row.type),
    status: 'issued',
    issuedAt,
    gibCode: `MOCK-${req.idempotencyKey}`,
    integrator: row.integrator ?? 'parasut',
    integratorError: null,
    updatedAt: issuedAt,
  };
  mutableStore.set(req.invoiceId, issued);
  return issued;
}

export async function sendInvoice(
  req: SendInvoiceRequest,
): Promise<InvoiceRow> {
  await wait(SIMULATED_LATENCY_MS);
  const row = mutableStore.get(req.invoiceId);
  if (!row) throw new Error(`Invoice not found: ${req.invoiceId}`);
  if (row.status === 'draft') {
    throw new Error('Draft invoice cannot be sent — issue first.');
  }
  const delivery: InvoiceDeliveryStatus =
    req.channel === 'email' ? 'sent_email' : 'sent_whatsapp';
  const updated: InvoiceRow = {
    ...row,
    deliveryStatus: delivery,
    status: row.status === 'issued' ? 'sent' : row.status,
    updatedAt: new Date().toISOString(),
  };
  mutableStore.set(req.invoiceId, updated);
  return updated;
}

export async function voidInvoice(
  req: VoidInvoiceRequest,
): Promise<InvoiceRow> {
  await wait(SIMULATED_LATENCY_MS);
  const row = mutableStore.get(req.invoiceId);
  if (!row) throw new Error(`Invoice not found: ${req.invoiceId}`);
  if (row.status === 'paid') {
    throw new Error('Paid invoice cannot be voided — issue a correction invoice.');
  }
  const updated: InvoiceRow = {
    ...row,
    status: 'voided',
    balanceMinor: 0,
    notes: row.notes ? `${row.notes}\nİptal: ${req.reason}` : `İptal: ${req.reason}`,
    updatedAt: new Date().toISOString(),
  };
  mutableStore.set(req.invoiceId, updated);
  return updated;
}

export async function downloadInvoice(
  req: DownloadInvoiceRequest,
): Promise<{ url: string; filename: string }> {
  await wait(SIMULATED_LATENCY_MS);
  const row = mutableStore.get(req.invoiceId);
  if (!row) throw new Error(`Invoice not found: ${req.invoiceId}`);
  if (!row.pdfUrl && req.format === 'pdf') {
    throw new Error('PDF not available for draft invoice.');
  }
  const filename = `${row.invoiceNumber ?? row.id}.${req.format === 'pdf' ? 'pdf' : 'xml'}`;
  const url = req.format === 'pdf' ? (row.pdfUrl ?? '#') : `${row.pdfUrl ?? '#'}.xml`;
  return { url, filename };
}

export function buildIssueIdempotencyKey(
  invoiceId: string,
  attemptNo: number,
): string {
  return `${invoiceId}:${attemptNo}`;
}

/**
 * Status display helpers — kept feature-side for consistency between page and
 * any future widget. Lovable composite'e geçirilmez (presentation = labels.ts).
 */
export function statusSummary(rows: readonly InvoiceRow[]): Record<InvoiceStatus, number> {
  const acc: Record<InvoiceStatus, number> = {
    draft: 0,
    issued: 0,
    sent: 0,
    paid: 0,
    voided: 0,
    failed: 0,
  };
  for (const r of rows) acc[r.status] += 1;
  return acc;
}
